import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    // Browsers can leave the autoplay attribute deferred for videos that load
    // outside the viewport, so playback is driven by visibility instead
    this.#intersectionObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];

      if (!entry) return;

      if (entry.isIntersecting && !prefersReducedMotion()) {
        this.refs.videoElement.play().catch(() => {});
      } else {
        this.refs.videoElement.pause();
      }
    });
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
  }

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
